import React, { useEffect, useMemo, useState } from "react";
import RowActions from "../components/RowActions";
import { usePublishScreenData } from "../utils/aiScreenContext";
import api from "../api/client";
import { Script, ScriptRun, Device } from "../types";
import { formatRelative } from "../utils/datetime";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import ScriptSchedulesPanel from "../components/ScriptSchedulesPanel";

export default function Scripts() {
  const { user } = useAuth();
  // The screen ignored the customer selector entirely - reported as
  // "Devices -> Scripts not honoring customer selector and instead shows all
  // scripts".
  //
  // The LIBRARY is deliberately global: a script like "restart the print
  // spooler" is written once and run anywhere, and Script has no customer_id
  // by design. What has to follow the selector is everything ABOUT a
  // customer - the run history, and the device list you pick from when
  // launching. Filtering the library too would hide a tech's own scripts
  // the moment they picked a customer.
  const { currentCustomerId } = useCustomer();
  const canManage = user?.role === "admin" || user?.role === "technician";
  const canDelete = user?.role === "admin";

  const [tab, setTab] = useState<"library" | "schedules" | "history">("library");
  const [scripts, setScripts] = useState<Script[]>([]);
  // For the Library picker in the editor. Loaded once - the list is small and
  // does not change while somebody is writing a script.
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);
  const [runs, setRuns] = useState<ScriptRun[]>([]);
  const [editing, setEditing] = useState<Partial<Script> | null>(null);
  const [running, setRunning] = useState<Script | null>(null);
  const [viewingRun, setViewingRun] = useState<ScriptRun | null>(null);
  const libSort = useSort("name");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Tab-aware, because this screen shows two
  // different tables and publishing the wrong one is worse than
  // publishing none. Projected explicitly: machine output and
  // credential fields must not reach a prompt.
  usePublishScreenData(
    tab === "history" ? "Script run history" : "Script library",
    useMemo(
      () =>
        tab === "history"
          ? runs.map((r) => ({
              id: r.id,
              script: r.script_name,
              device: r.device_hostname,
              status: r.status,
              queued_at: r.queued_at,
              completed_at: r.completed_at ?? null,
              exit_code: r.exit_code ?? null,
              duration_ms: r.duration_ms ?? null,
              // NOT stdout/stderr. A script's output is whatever it printed -
              // including, on a bad day, a credential it echoed.
            }))
          : scripts.map((s) => ({
              id: s.id,
              name: s.name,
              language: (s as any).language ?? null,
              description: s.description ?? null,
            })),
      [tab, runs, scripts],
    ),
    `${tab} tab`,
  );

  // The LIBRARY follows the customer selector now.
  //
  // REPORTED: "the scripts menu item is showing all scripts for all customers
  // regardless of the customer selector. The scripts should honor the
  // selector and should only show scripts for that customer library."
  //
  // This overrides the earlier decision recorded below, which kept the
  // library global on the reasoning that a script like "restart the print
  // spooler" is written once and run anywhere. That reasoning is why SHARED
  // scripts (no customer) still appear rather than vanishing the moment a
  // customer is picked - and why there is a toggle to exclude them for a
  // strict single-library view.
  const [onlyThisCustomer, setOnlyThisCustomer] = useState(false);
  const loadScripts = () =>
    api
      .get("/api/v1/scripts", {
        params: {
          ...(currentCustomerId ? { customer_id: currentCustomerId } : {}),
          ...(currentCustomerId && onlyThisCustomer ? { include_shared: false } : {}),
        },
      })
      .then((r) => setScripts(r.data));
  const loadRuns = () =>
    api
      .get("/api/v1/script-runs", {
        params: {
          limit: 200,
          ...(currentCustomerId ? { customer_id: currentCustomerId } : {}),
        },
      })
      .then((r) => setRuns(r.data));
  useEffect(() => {
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers((r.data || []).map((c: any) => ({ id: c.id, name: c.name }))))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    loadScripts();
    loadRuns();
    // Re-fetch the history when the selector moves, or it keeps showing the
    // customer you were looking at before. `onlyThisCustomer` is in here too:
    // it changes what the LIBRARY query asks for, so leaving it out makes the
    // toggle look broken until something else triggers a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomerId, onlyThisCustomer]);

  const saveScript = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await api.put(`/api/v1/scripts/${editing.id}`, {
          customer_id: editing.customer_id ?? null,
          name: editing.name,
          description: editing.description,
          language: editing.language,
          body: editing.body,
          default_timeout_seconds: editing.default_timeout_seconds,
          enabled: editing.enabled,
        });
      } else {
        await api.post("/api/v1/scripts", {
          customer_id: editing.customer_id ?? null,
          name: editing.name,
          description: editing.description || "",
          language: editing.language || "powershell",
          body: editing.body || "",
          default_timeout_seconds: editing.default_timeout_seconds || 300,
          enabled: editing.enabled !== false,
        });
      }
      setEditing(null);
      loadScripts();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Save failed");
    }
  };

  const deleteScript = async (s: Script) => {
    if (!window.confirm(`Delete script "${s.name}"? This cannot be undone.`)) return;
    await api.delete(`/api/v1/scripts/${s.id}`);
    loadScripts();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Scripts</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            PowerShell / Batch script library. Every execution is recorded in the audit log.
          </p>
        </div>
        {canManage && tab === "library" && (
          <button
            onClick={() =>
              setEditing({
                // Default into the library you are looking at. Creating a
                // script while scoped to a customer and having it land in the
                // shared pile is the bug this whole change is about, in
                // miniature.
                customer_id: currentCustomerId ?? null,
                name: "",
                language: "powershell",
                body: "",
                default_timeout_seconds: 300,
                enabled: true,
              })
            }
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
          >
            + New script
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-4 border-b dark:border-gray-700">
        {(["library", "schedules", "history"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              tab === t
                ? "border-blue-600 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            {t === "library"
              ? `Library (${scripts.length})`
              : t === "schedules"
                ? "Schedules"
                : `Run history (${runs.length})`}
          </button>
        ))}
        {currentCustomerId && tab === "library" && (
          <label className="ml-auto flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 pb-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyThisCustomer}
              onChange={(e) => setOnlyThisCustomer(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            {/* Shared scripts are IN this customer's library by design - see
                loadScripts. The toggle is for the times you want to see only
                what belongs to them. */}
            Hide shared scripts
          </label>
        )}
      </div>

      {tab === "schedules" && (
        <ScriptSchedulesPanel scripts={scripts} canManage={canManage} />
      )}

      {tab === "library" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          {scripts.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-10">
              {currentCustomerId && onlyThisCustomer
                ? "No scripts belong to this customer yet. Untick “Hide shared scripts” to see the shared library, or create one here."
                : currentCustomerId
                  ? "No scripts in this customer’s library, and none shared."
                  : "No scripts yet. Click “+ New script” to add one."}
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={libSort.sort} onSort={libSort.toggle} />
                  <SortableTh label="Library" sortKey="customer_name" sort={libSort.sort} onSort={libSort.toggle} hide="hidden md:table-cell" />
                  <SortableTh label="Description" sortKey="description" sort={libSort.sort} onSort={libSort.toggle} hide="hidden lg:table-cell" />
                  <SortableTh label="Lang" sortKey="language" sort={libSort.sort} onSort={libSort.toggle} hide="hidden md:table-cell" />
                  <SortableTh label="Updated" sortKey="updated_at" sort={libSort.sort} onSort={libSort.toggle} hide="hidden lg:table-cell" />
                  <SortableTh label="Enabled" sortKey="enabled" sort={libSort.sort} onSort={libSort.toggle} hide="hidden sm:table-cell" />
                  {canManage && <th className="px-4 py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortRows(scripts, libSort.sort, (s, key) => {
                  switch (key) {
                    case "customer_name":
                      // Shared sorts last under a name that cannot collide
                      // with a real customer.
                      return (s.customer_name || "￿ shared").toLowerCase();
                    case "description":
                      return (s.description || "").toLowerCase();
                    case "language":
                      return (s.language || "").toLowerCase();
                    case "updated_at":
                      return s.updated_at ? Date.parse(s.updated_at) : 0;
                    case "enabled":
                      return s.enabled ? 1 : 0;
                    default:
                      return (s.name || "").toLowerCase();
                  }
                }).map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                      {/* Script names run long, and this was measured at 313px
                          of a 366px phone container - so hiding the other four
                          columns bought almost nothing. Capped below sm only;
                          full name on hover. */}
                      <div className="max-w-[11rem] sm:max-w-none truncate" title={s.name}>
                        {s.name}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs hidden md:table-cell">
                      {/* A shared script is not "no customer" to a reader, it
                          is a deliberate choice - so it gets a word rather
                          than a dash. */}
                      {s.customer_name ? (
                        <span className="text-gray-700 dark:text-gray-300">{s.customer_name}</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                          Shared
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 text-xs max-w-xs truncate hidden lg:table-cell">
                      {s.description || "—"}
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <LangBadge language={s.language} />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                      {formatRelative(s.updated_at)}
                    </td>
                    <td className="px-4 py-2.5 text-xs hidden sm:table-cell">
                      {s.enabled ? (
                        <span className="text-green-700 dark:text-green-400">Enabled</span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Disabled</span>
                      )}
                    </td>
                    {canManage && (
                      <td className="px-4 py-2.5 text-right">
                        {/* Three text buttons plus whitespace-nowrap measured
                            167px, holding the whole table 9px past a 366px
                            phone container. From md up they stay inline; below
                            md they go behind the kebab, which is the same
                            treatment Alerts and Certificates already use. */}
                        <div className="hidden md:block whitespace-nowrap">
                          <button
                            onClick={() => setRunning(s)}
                            disabled={!s.enabled}
                            className="text-xs bg-blue-600 text-white px-2 py-1 rounded mr-1 hover:bg-blue-700 disabled:opacity-40"
                          >
                            Run
                          </button>
                          <button
                            onClick={() => setEditing({ ...s })}
                            className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/40 mr-1"
                          >
                            Edit
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => deleteScript(s)}
                              className="text-xs border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                        <div className="md:hidden flex justify-end">
                          <RowActions
                            actions={[
                              {
                                label: "Run",
                                onClick: () => setRunning(s),
                                disabled: !s.enabled,
                                title: s.enabled
                                  ? "Run this script on selected devices"
                                  : "This script is disabled",
                              },
                              { label: "Edit", onClick: () => setEditing({ ...s }) },
                              {
                                label: "Delete",
                                onClick: () => deleteScript(s),
                                danger: true,
                                hidden: !canDelete,
                              },
                            ]}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "history" && <RunHistoryTable runs={runs} onView={setViewingRun} />}

      {editing && (
        <ScriptEditor
          draft={editing}
          customers={customers}
          onChange={setEditing}
          onSave={saveScript}
          onCancel={() => setEditing(null)}
        />
      )}
      {running && (
        <RunScriptModal
          script={running}
          onClose={() => setRunning(null)}
          onLaunched={() => {
            setRunning(null);
            loadRuns();
            setTab("history");
          }}
        />
      )}
      {viewingRun && <RunDetailModal run={viewingRun} onClose={() => setViewingRun(null)} />}
    </div>
  );
}

function AIGenerateButton({
  language,
  onResult,
}: {
  language: string;
  onResult: (body: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/api/v1/ai/script-generate", {
        description: desc,
        language,
      });
      onResult(r.data.body);
      setOpen(false);
      setDesc("");
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Generate failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs px-2 py-0.5 bg-violet-600 hover:bg-violet-700 text-white rounded font-medium"
        title="Describe what the script should do; AI will draft it."
      >
        ⚡ Generate with AI
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-5 w-full max-w-lg space-y-3">
            <h3 className="text-lg font-semibold dark:text-gray-100">Generate {language} script</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Describe what the script should do in plain English. AI drafts the body; you
              still review + save it.
            </p>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={5}
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded p-2 text-sm"
              placeholder="e.g. Find all processes using port 8080 and kill them. Return success even if no process was using the port."
            />
            {error && (
              <div className="text-xs bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 p-2 rounded">{error}</div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="px-3 py-1.5 border dark:border-gray-600 dark:text-gray-300 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy || !desc.trim()}
                className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-sm font-medium disabled:opacity-50"
              >
                {busy ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function LangBadge({ language }: { language: string }) {
  const cls =
    language === "powershell"
      ? "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
      : language === "batch"
        ? "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
        : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{language}</span>;
}

function ScriptEditor({
  draft,
  customers,
  onChange,
  onSave,
  onCancel,
}: {
  draft: Partial<Script>;
  customers: { id: number; name: string }[];
  onChange: (s: Partial<Script>) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4 dark:text-gray-100">
          {draft.id ? `Edit "${draft.name}"` : "New script"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">Name</label>
            <input
              type="text"
              value={draft.name || ""}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
              Library
            </label>
            <select
              value={draft.customer_id == null ? "" : String(draft.customer_id)}
              onChange={(e) =>
                onChange({
                  ...draft,
                  customer_id: e.target.value === "" ? null : Number(e.target.value),
                })
              }
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Shared — every customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">Language</label>
            <select
              value={draft.language || "powershell"}
              onChange={(e) => onChange({ ...draft, language: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            >
              <option value="powershell">PowerShell</option>
              <option value="batch">Batch (cmd.exe)</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">Description</label>
          <input
            type="text"
            value={draft.description || ""}
            onChange={(e) => onChange({ ...draft, description: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
              Script body ({draft.language === "batch" ? ".cmd" : ".ps1"})
            </label>
            <AIGenerateButton
              language={draft.language || "powershell"}
              onResult={(body) => onChange({ ...draft, body })}
            />
          </div>
          <textarea
            value={draft.body || ""}
            rows={14}
            onChange={(e) => onChange({ ...draft, body: e.target.value })}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-xs font-mono"
            placeholder={
              draft.language === "batch"
                ? "@echo off\necho Hello from %COMPUTERNAME%\nexit /b 0"
                : 'Write-Output "Hello from $env:COMPUTERNAME"\nexit 0'
            }
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
              Timeout (seconds)
            </label>
            <input
              type="number"
              min={10}
              max={3600}
              value={draft.default_timeout_seconds || 300}
              onChange={(e) =>
                onChange({ ...draft, default_timeout_seconds: Number(e.target.value) })
              }
              className="w-32 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-xs mt-5">
            <input
              type="checkbox"
              checked={draft.enabled !== false}
              onChange={(e) => onChange({ ...draft, enabled: e.target.checked })}
            />
            <span>Enabled (can be selected for runs)</span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/40"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!draft.name || !draft.body}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {draft.id ? "Save changes" : "Create script"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunScriptModal({
  script,
  onClose,
  onLaunched,
}: {
  script: Script;
  onClose: () => void;
  onLaunched: () => void;
}) {
  // Scoped to the selected customer. Offering every online device in the
  // estate makes it easy to run a script on the wrong company's servers,
  // which is the sort of mistake that is not recoverable.
  const { currentCustomerId } = useCustomer();
  const [devices, setDevices] = useState<Device[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    api
      .get("/api/v1/devices", {
        params: {
          status: "online",
          ...(currentCustomerId ? { customer_id: currentCustomerId } : {}),
        },
      })
      .then((r) => setDevices(r.data));
  }, [currentCustomerId]);

  const filtered = useMemo(() => {
    if (!search) return devices;
    const q = search.toLowerCase();
    return devices.filter(
      (d) =>
        d.hostname.toLowerCase().includes(q) || (d.customer_name || "").toLowerCase().includes(q),
    );
  }, [devices, search]);

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const launch = async () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `About to run "${script.name}" on ${selected.size} device(s).\n\n` +
          `This is recorded in the audit log under your account.`,
      )
    )
      return;
    setLaunching(true);
    try {
      await api.post(`/api/v1/scripts/${script.id}/run`, { device_ids: Array.from(selected) });
      onLaunched();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Launch failed");
      setLaunching(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] flex flex-col">
        <h2 className="text-lg font-semibold mb-1 dark:text-gray-100">Run "{script.name}"</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Select one or more online devices. Each selection runs the script in parallel.
        </p>
        <input
          type="text"
          placeholder="Filter by hostname or customer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mb-3"
        />
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden flex-1 overflow-y-auto mb-4">
          <table className="min-w-full text-sm">
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                  onClick={() => toggle(d.id)}
                >
                  <td className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => toggle(d.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{d.hostname}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{d.customer_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    <span
                      className={`px-2 py-0.5 rounded-full ${
                        d.status === "online"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                          : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                      }`}
                    >
                      {d.status}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td className="px-3 py-8 text-center text-gray-400 dark:text-gray-500 text-sm" colSpan={4}>
                    No online devices match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">{selected.size} selected</span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/40"
            >
              Cancel
            </button>
            <button
              onClick={launch}
              disabled={selected.size === 0 || launching}
              className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {launching
                ? "Launching…"
                : `Run on ${selected.size} device${selected.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RunHistoryTable({ runs, onView }: { runs: ScriptRun[]; onView: (r: ScriptRun) => void }) {
  const histSort = useSort("script_name");
  const statusBadge = (s: string) => {
    const cls =
      s === "completed"
        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
        : s === "failed" || s === "timed_out"
          ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
          : s === "running" || s === "sent"
            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
    return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{s}</span>;
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
          <tr>
            <SortableTh label="Script" sortKey="script_name" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="Device" sortKey="device" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="Status" sortKey="status" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="Exit" sortKey="exit_code" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="Duration" sortKey="duration_ms" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="When" sortKey="queued_at" sort={histSort.sort} onSort={histSort.toggle} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {sortRows(runs, histSort.sort, (r, key) => {
            switch (key) {
              case "device":
                return (r.device_hostname || `#${r.device_id}`).toLowerCase();
              case "status":
                return (r.status || "").toLowerCase();
              case "exit_code":
                return r.exit_code ?? 0;
              case "duration_ms":
                return r.duration_ms ?? 0;
              case "queued_at":
                return r.queued_at ? Date.parse(r.queued_at) : 0;
              default:
                return (r.script_name || "").toLowerCase();
            }
          }).map((r) => (
            <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer" onClick={() => onView(r)}>
              <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                {r.script_name || "(deleted)"}
              </td>
              <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                {r.device_hostname || `#${r.device_id}`}
              </td>
              <td className="px-4 py-2.5">{statusBadge(r.status)}</td>
              <td className="px-4 py-2.5 text-xs">{r.exit_code ?? "—"}</td>
              <td className="px-4 py-2.5 text-xs">
                {r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}
              </td>
              <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                {formatRelative(r.queued_at)}
              </td>
            </tr>
          ))}
          {runs.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
                No runs yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RunDetailModal({ run, onClose }: { run: ScriptRun; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold dark:text-gray-100">
            {run.script_name || "(deleted script)"} · {run.device_hostname || `#${run.device_id}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200">
            ✕
          </button>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Status: <span className="font-medium text-gray-800 dark:text-gray-200">{run.status}</span> · Exit:{" "}
          <span className="font-mono">{run.exit_code ?? "—"}</span> · Duration:{" "}
          {run.duration_ms ? `${(run.duration_ms / 1000).toFixed(2)}s` : "—"}
        </div>
        <div className="grid grid-cols-1 gap-3 flex-1 overflow-y-auto">
          <div>
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">stdout</div>
            <pre className="bg-gray-50 dark:bg-gray-900/50 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded p-3 text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto">
              {run.stdout || "(empty)"}
            </pre>
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">stderr</div>
            <pre className="bg-red-50 dark:bg-red-900/20 dark:text-red-200 border border-red-100 dark:border-red-900/50 rounded p-3 text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto">
              {run.stderr || "(empty)"}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
